import React, { useState, useEffect } from "react";
import { Box, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import ReportsSidebar, { REPORT_TABS } from "../../components/Reports/ReportsSidebar";
import ReportsTable from "../../components/Reports/ReportsTable";
import BroadcastsReport from "../../components/Reports/BroadcastsReport";
import PanelHeading from "../../components/Navbar/PanelHeading";

const Reports = () => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState("teamMembers");
  // Phase 6 ("Client UI") QR gating.
  const isQrConnectedWorkspace = useSelector((s) => s.workspace?.currentWorkspace?.whatsappConnectionType === "baileys");
  // Covers switching to (or the workspace itself switching into) a QR connection while already
  // on the Broadcasts tab — the default tab is "teamMembers", so this only matters for that
  // specific already-selected case, not the initial load.
  useEffect(() => {
    if (isQrConnectedWorkspace && selectedTab === "broadcasts") setSelectedTab("teamMembers");
  }, [isQrConnectedWorkspace, selectedTab]);
  // Same REPORT_TABS list the sidebar uses -- single source of truth so the
  // content header's icon/description can't drift from the sidebar's own.
  const currentTab = REPORT_TABS.find((t) => t.key === selectedTab);
  const CurrentTabIcon = currentTab?.icon;
  // Whether ReportsTable is currently showing a category drill-down instead
  // of the Overview table -- both now own their own bounded internal
  // scroll (2026-09-05), so this file itself no longer branches on it; only
  // the setter is still needed, to hand ReportsTable a place to report the
  // change up to (kept in case a future consumer here needs to read it).
  const [, setIsCategoryMode] = useState(false);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        // ReportsSidebar already goes full-width at xs (meant to stack as a
        // strip above the content, not a side rail) -- this was still fixed
        // "row" always, so on mobile the full-width sidebar ate the whole
        // row and pushed the real content off-screen (2026-09-08 bug fix:
        // "the left panel opens for some reason").
        flexDirection: { xs: "column", sm: "row" },
        overflow: { xs: "auto", sm: "hidden" },
        color: "text.primary",
        backgroundColor: "background.default",
      }}
    >
      <ReportsSidebar selectedTab={selectedTab} onSelectTab={setSelectedTab} broadcastsAllowed={!isQrConnectedWorkspace} />

      {/* Column wrapper: heading bar on top, scrollable content below --
          both sit to the right of ReportsSidebar, never above it. */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <PanelHeading
            title={currentTab?.label || "Team Members"}
            subtitle={currentTab?.description}
            icon={
              CurrentTabIcon && (
                <CurrentTabIcon size="100%" color={theme.palette.text.secondary} variant="Linear" />
              )
            }
            variant="full"
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            pt: { xs: 2, sm: 2, md: 1, lg: 1, xl: 1 },
            /* Extra room above bottom nav / viewport edge; safe-area on notched phones */
            pb: {
              xs: "calc(80px + env(safe-area-inset-bottom, 0px))",
              sm: "calc(80px + env(safe-area-inset-bottom, 0px))",
              md: 6,
              lg: 6,
              xl: 6,
            },
            overflowX: "hidden",
            // Overview used to be the one exception that scrolled the WHOLE
            // page (2026-08-17 ask: "the whole page scrolls, not a box
            // inside it") -- reversed 2026-09-05 so the sticky table header
            // could actually work (Rahim's explicit go-ahead: "i dont mind
            // the parent page scroll bar to disapear"). Both Reports
            // sub-tables now own their own bounded internal scroll
            // (ReportsTable.jsx's TableContainer for Overview,
            // CategoryReportDetail.jsx for the category drill-down) --
            // this outer box only still scrolls for the Broadcasts tab,
            // which has no internal height cap of its own.
            overflowY: selectedTab !== "broadcasts" ? "hidden" : "auto",
            boxSizing: "border-box",
          }}
        >
          {/* Inner container to add consistent left/right margin for the reports content --
              matches Tasks.jsx's own page wrapper exactly (no maxWidth cap, same small
              px) so this table gets the same near-edge-to-edge width Tasks' table has,
              instead of sitting centered with a big gray gutter on wide screens. */}
          <Box
            sx={{
              width: "100%",
              mt: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
              px: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
              boxSizing: "border-box",
            }}
          >
            {selectedTab === "broadcasts" ? <BroadcastsReport /> : <ReportsTable onCategoryModeChange={setIsCategoryMode} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Reports;
